import { useEffect, useState } from 'react';
import { pharmacyApi } from './api/pharmacy.api';

export interface ConnectionStatus {
    status: 'connected' | 'slow' | 'disconnected' | 'checking';
    latency?: number;
    lastSync?: Date;
    error?: string;
}

export interface PharmacyConnectionInfo {
    pharmacyId: string;
    connectionStatus: ConnectionStatus;
}

// Browser-compatible EventEmitter replacement
type EventCallback = (...args: any[]) => void;

class BrowserEventEmitter {
    private listeners: Map<string, Set<EventCallback>> = new Map();

    on(event: string, callback: EventCallback): void {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event)!.add(callback);
    }

    off(event: string, callback: EventCallback): void {
        this.listeners.get(event)?.delete(callback);
    }

    emit(event: string, ...args: unknown[]): void {
        this.listeners.get(event)?.forEach(callback => callback(...args));
    }
}

class PharmacyConnectionService extends BrowserEventEmitter {
    private connections: Map<string, ConnectionStatus> = new Map();
    private checkIntervals: Map<string, NodeJS.Timeout> = new Map();
    private readonly CHECK_INTERVAL = 120000; // 30 seconds
    private readonly SLOW_THRESHOLD = 500; // 500ms is considered slow
    private readonly TIMEOUT_THRESHOLD = 5000; // 5 seconds timeout

    constructor() {
        super();
        this.initialize();
    }

    private initialize() {
        // Listen for visibility changes to pause/resume monitoring
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.pauseAllMonitoring();
            } else {
                this.resumeAllMonitoring();
            }
        });
    }

    public startMonitoring(pharmacyId: string) {
        if (this.checkIntervals.has(pharmacyId)) {
            return; // Already monitoring
        }

        // Initial check with a small delay to ensure auth is ready
        setTimeout(() => {
            this.checkConnection(pharmacyId);
        }, 500);

        // Set up interval
        const interval = setInterval(() => {
            this.checkConnection(pharmacyId);
        }, this.CHECK_INTERVAL);

        this.checkIntervals.set(pharmacyId, interval);
    }

    public stopMonitoring(pharmacyId: string) {
        const interval = this.checkIntervals.get(pharmacyId);
        if (interval) {
            clearInterval(interval);
            this.checkIntervals.delete(pharmacyId);
        }
        this.connections.delete(pharmacyId);
    }

    public stopAllMonitoring() {
        this.checkIntervals.forEach((interval) => clearInterval(interval));
        this.checkIntervals.clear();
        this.connections.clear();
    }

    private pauseAllMonitoring() {
        this.checkIntervals.forEach((interval) => clearInterval(interval));
    }

    private resumeAllMonitoring() {
        const pharmacyIds = Array.from(this.checkIntervals.keys());
        this.checkIntervals.clear();
        pharmacyIds.forEach(id => this.startMonitoring(id));
    }

    private async checkConnection(pharmacyId: string) {
        const startTime = Date.now();

        try {
            // Update status to checking
            this.updateConnectionStatus(pharmacyId, {
                status: 'checking',
                lastSync: this.connections.get(pharmacyId)?.lastSync,
            });

            // Create a timeout promise
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Connection timeout')), this.TIMEOUT_THRESHOLD);
            });

            // Race between the actual check and timeout
            await Promise.race([
                pharmacyApi.checkConnection(pharmacyId),
                timeoutPromise,
            ]);

            const latency = Date.now() - startTime;
            const status: ConnectionStatus['status'] =
                latency > this.SLOW_THRESHOLD ? 'slow' : 'connected';

            this.updateConnectionStatus(pharmacyId, {
                status,
                latency,
                lastSync: new Date(),
            });
        } catch (error) {
            this.updateConnectionStatus(pharmacyId, {
                status: 'disconnected',
                latency: Date.now() - startTime,
                error: error instanceof Error ? error.message : 'Unknown error',
                lastSync: this.connections.get(pharmacyId)?.lastSync,
            });
        }
    }

    private updateConnectionStatus(pharmacyId: string, status: ConnectionStatus) {
        const prevStatus = this.connections.get(pharmacyId);
        this.connections.set(pharmacyId, status);

        // Emit event if status changed
        if (!prevStatus || prevStatus.status !== status.status) {
            this.emit('statusChanged', { pharmacyId, connectionStatus: status });
        }

        // Always emit update event
        this.emit('statusUpdated', { pharmacyId, connectionStatus: status });
    }

    public getConnectionStatus(pharmacyId: string): ConnectionStatus | undefined {
        return this.connections.get(pharmacyId);
    }

    public getAllConnectionStatuses(): PharmacyConnectionInfo[] {
        return Array.from(this.connections.entries()).map(([pharmacyId, connectionStatus]) => ({
            pharmacyId,
            connectionStatus,
        }));
    }

    // Manual refresh method
    public async refreshConnection(pharmacyId: string): Promise<void> {
        await this.checkConnection(pharmacyId);
    }

    // Get average latency for a pharmacy
    public getAverageLatency(pharmacyId: string): number | undefined {
        const status = this.connections.get(pharmacyId);
        return status?.latency;
    }

    // Check if pharmacy is online
    public isPharmacyOnline(pharmacyId: string): boolean {
        const status = this.connections.get(pharmacyId);
        return status?.status === 'connected' || status?.status === 'slow';
    }

    // Get connection quality
    public getConnectionQuality(pharmacyId: string): 'excellent' | 'good' | 'poor' | 'offline' | 'unknown' {
        const status = this.connections.get(pharmacyId);
        if (!status) return 'unknown';

        if (status.status === 'disconnected') return 'offline';
        if (status.status === 'checking') return 'unknown';
        if (!status.latency) return 'unknown';

        if (status.latency < 100) return 'excellent';
        if (status.latency < 300) return 'good';
        return 'poor';
    }
}

// Create singleton instance
export const pharmacyConnectionService = new PharmacyConnectionService();

// React hook for using connection status
export function usePharmacyConnection(pharmacyId: string | undefined) {
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | undefined>();

    useEffect(() => {
        if (!pharmacyId) return;

        // Set initial checking status
        setConnectionStatus({
            status: 'checking',
            lastSync: undefined,
        });

        // Start monitoring
        pharmacyConnectionService.startMonitoring(pharmacyId);

        // Get initial status if available
        const initialStatus = pharmacyConnectionService.getConnectionStatus(pharmacyId);
        if (initialStatus) {
            setConnectionStatus(initialStatus);
        }

        // Listen for updates
        const handleUpdate = ({ pharmacyId: updatedId, connectionStatus: status }: PharmacyConnectionInfo) => {
            if (updatedId === pharmacyId) {
                setConnectionStatus(status);
            }
        };

        pharmacyConnectionService.on('statusUpdated', handleUpdate);

        return () => {
            pharmacyConnectionService.off('statusUpdated', handleUpdate);
            // Don't stop monitoring on cleanup as other components might be using it
        };
    }, [pharmacyId]);

    const refresh = async () => {
        if (pharmacyId) {
            await pharmacyConnectionService.refreshConnection(pharmacyId);
        }
    };

    return { connectionStatus, refresh };
}